/**
 * ハッシュ語彙の唯一の定義点（FR-LINK-01・FR-LINK-03・FR-LB-02）。純関数・DOM 非依存。
 *
 * 語彙: `#page=12&z=2.5&x=0.31&y=0.62&lb=1`
 *   page  1 始まりのページ番号（常に書く）
 *   z     倍率（1 以下なら書かない — 既存の `#page=12` と 1 バイトも変わらない）
 *   x/y   注視ページ内の正規化位置（z が無ければ書かない）
 *   lb    ライトボックスが開いているか
 *
 * 2 台目以降のビューワは接頭辞つき（`book2.page`）。接頭辞の確保は hash-router.ts。
 *
 * 直列化を URLSearchParams に任せていない理由: `#introduction` のような素のアンカーを
 * `introduction=` へ書き換えてしまい、他人のフラグメントを壊すため。値の無いキーは
 * 値の無いまま出す。
 */

export interface HashState {
  page: number;
  /** 倍率。1 以下・未指定は undefined。 */
  zoom?: number;
  /** 注視ページ内の正規化位置（0〜1）。 */
  x?: number;
  y?: number;
  lightbox?: boolean;
}

/** 倍率の小数桁（zoomStep 1.5 の冪 1.5 / 2.25 / 3.38 / 4 が可逆）。 */
const ZOOM_DIGITS = 2;
/** 位置の小数桁（0.001 = ページ幅の 0.1% ≒ サブピクセル）。 */
const POSITION_DIGITS = 3;

type Entry = [key: string, value: string | null];

/** ハッシュ文字列から状態を読む。ページ番号が無ければ null（＝この URL は指定なし）。 */
export function parseHash(hash: string, prefix: string): HashState | null {
  const entries = parseEntries(hash);
  const get = (key: string): string | null => {
    const hit = entries.find(([name]) => name === prefix + key);
    return hit ? hit[1] : null;
  };

  const page = toInteger(get('page'));
  if (page === null) return null;

  const state: HashState = { page };
  const zoom = toNumber(get('z'));
  if (zoom !== null && zoom > 1) {
    state.zoom = zoom;
    // z があって x/y が欠ける不完全な URL はページ中央に寄せる。
    state.x = clamp01(toNumber(get('x')) ?? 0.5);
    state.y = clamp01(toNumber(get('y')) ?? 0.5);
  }
  if (get('lb') === '1') state.lightbox = true;
  return state;
}

/**
 * 既存のハッシュへ状態を書き込み、`#` 付きの文字列を返す。
 * 他のビューワのキー・素のアンカーは順序ごと保存する。
 * 自分のキーは必ず set か delete のどちらかを通す — 拡大してから戻したとき、
 * 古い z/x/y が消えずに次の共有 URL へ紛れ込むのを防ぐ。
 */
export function applyHash(hash: string, prefix: string, state: HashState): string {
  const entries = parseEntries(hash);
  const put = (key: string, value: string | null): void => {
    const name = prefix + key;
    const index = entries.findIndex(([existing]) => existing === name);
    if (value === null) {
      if (index >= 0) entries.splice(index, 1);
    } else if (index >= 0) {
      entries[index] = [name, value];
    } else {
      entries.push([name, value]);
    }
  };

  const zoomed = state.zoom !== undefined && state.zoom > 1;
  put('page', String(state.page));
  put('z', zoomed ? format(state.zoom as number, ZOOM_DIGITS) : null);
  put('x', zoomed ? format(clamp01(state.x ?? 0.5), POSITION_DIGITS) : null);
  put('y', zoomed ? format(clamp01(state.y ?? 0.5), POSITION_DIGITS) : null);
  put('lb', state.lightbox === true ? '1' : null);

  const body = serializeEntries(entries);
  return body === '' ? '' : `#${body}`;
}

/** 状態が等しいか（不要な書き戻しと二重通知を避けるため）。 */
export function sameHashState(a: HashState | null, b: HashState | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.page === b.page &&
    format(a.zoom ?? 1, ZOOM_DIGITS) === format(b.zoom ?? 1, ZOOM_DIGITS) &&
    format(a.x ?? 0.5, POSITION_DIGITS) === format(b.x ?? 0.5, POSITION_DIGITS) &&
    format(a.y ?? 0.5, POSITION_DIGITS) === format(b.y ?? 0.5, POSITION_DIGITS) &&
    (a.lightbox === true) === (b.lightbox === true)
  );
}

function parseEntries(hash: string): Entry[] {
  const body = hash.replace(/^#/, '');
  if (body === '') return [];
  return body
    .split('&')
    .filter((part) => part !== '')
    .map((part): Entry => {
      const eq = part.indexOf('=');
      if (eq < 0) return [decode(part), null];
      return [decode(part.slice(0, eq)), decode(part.slice(eq + 1))];
    });
}

function serializeEntries(entries: readonly Entry[]): string {
  return entries
    .map(([key, value]) => (value === null ? encode(key) : `${encode(key)}=${encode(value)}`))
    .join('&');
}

/** `+` を空白に読み替えない（ハッシュはフォーム送信ではない）。壊れた % は素通し。 */
function decode(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function encode(raw: string): string {
  return encodeURIComponent(raw);
}

function toInteger(raw: string | null): number | null {
  if (raw === null) return null;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) && value >= 1 ? value : null;
}

function toNumber(raw: string | null): number | null {
  if (raw === null) return null;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : null;
}

function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

/** 丸めて末尾ゼロを落とす（0.310 → "0.31"）。 */
function format(value: number, digits: number): string {
  const scale = 10 ** digits;
  return String(Math.round(value * scale) / scale);
}
